import React, { useState, useEffect, useCallback, useMemo } from 'react';
import uniqBy from 'lodash.uniqby';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';
import getCategories from '@core/spaceSelector/utils/getCategories';
import filterByCategory from '@core/spaceSelector/utils/filterByCategory';
import getSubCategories from '@core/spaceSelector/utils/getSubCategories';
import {
  Box,
  Separator,
  useTheme,
  Accordion,
  Area,
  Text,
} from '@ublend-npm/aulaui-core';
import useCategoryPreferences from '../hooks/useCategoryPreferences';
import SpaceList from './SpaceList';
import SpaceListAccordion from './SpaceListAccordion';
import StarSpaceOnboarding from './StarSpaceOnboarding';
import { StaffSpaces } from './StaffSpaces';

const Divider = () => <Separator sx={{ my: '0.5rem' }} />;
const starredCategory = {
  label: 'Starred spaces',
  order: -1,
};

// function to ensure space is valid
const isValidSpace = (space) =>
  space &&
  typeof space === 'object' &&
  'archived' in space &&
  Array.isArray(space.categories);

const SpaceSelector = ({
  spaces,
  onSpaceSelect,
  onSpaceStar,
  onSubCategorySelect,
  search = '',
  showArchived = false,
  sort = 'az',
  fetchRecentlyViewedSpaces,
}) => {
  // Remove local state for showArchived, filter, sortedBy
  const [fuse, setFuse] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [recentlyUnstarredSpaces, setRecentlyUnstarredSpaces] = useState([]);

  // Use search prop for filtering - memoized for performance
  const filteredSpaces = useMemo(() => {
    // Start with all valid spaces
    let validSpaces = spaces.filter(isValidSpace);

    // Apply archived filter
    if (!showArchived) {
      validSpaces = validSpaces.filter((s) => !s.archived);
    }

    // Apply search filter if search term exists
    if (!search || !fuse) {
      return validSpaces;
    }

    const fuseResults = fuse
      .search(search)
      .map((r) => r.item)
      .filter(isValidSpace);

    const directMatches = validSpaces.filter((space) =>
      space.name?.toLowerCase().includes(search.toLowerCase())
    );

    return uniqBy([...directMatches, ...fuseResults], 'id');
  }, [fuse, search, showArchived, spaces]);

  // Starred spaces - dynamically shows only starred spaces
  const starredSpacesForDisplay = useMemo(() => {
    return filteredSpaces
      .filter((s) => {
        // Check if space is starred in Redux state (source of truth)
        const isCurrentlyStarred = s.isStarred;
        // Check if it was recently unstarred in local state
        const isRecentlyUnstarred = recentlyUnstarredSpaces.some(
          (rs) => rs.id === s.id
        );
        // Only show if starred AND not recently unstarred
        return isCurrentlyStarred && !isRecentlyUnstarred;
      })
      .map((s) => ({ ...s, categories: [starredCategory] }))
      .sort((a, b) => {
        if (sort === 'az') {
          return a.name.localeCompare(b.name);
        } else if (sort === 'za') {
          return b.name.localeCompare(a.name);
        }
        return 0;
      });
  }, [filteredSpaces, recentlyUnstarredSpaces, sort]);

  // All other spaces - always show all spaces (not affected by starring)
  const allSpacesForDisplay = useMemo(() => {
    return [...filteredSpaces].sort((a, b) => {
      if (sort === 'az') {
        return a.name.localeCompare(b.name);
      } else if (sort === 'za') {
        return b.name.localeCompare(a.name);
      }
      return 0;
    });
  }, [filteredSpaces, sort]);

  let communitiesAndTrainingSpaces = [];
  // To handle the opening and closing of the accordions
  const [openedAccordions, setOpenedAccordions] = useState(
    new Set([starredCategory.label])
  );
  const { palette } = useTheme();
  const categories = useMemo(
    () => [starredCategory, ...getCategories(spaces)],
    [spaces]
  );
  const { categoryPreferences, setSubCategoriesPreference } =
    useCategoryPreferences();

  let primaryCategory = null;

  if (categories.length > 0) {
    [primaryCategory] = categories;
  }

  useEffect(() => {
    if (categoryPreferences && categoryPreferences.category) {
      const category = categories.find(
        (c) => c.label === categoryPreferences.category.label
      );
      if (category) {
        setSelectedCategory(category);
      }
    } else {
      setSelectedCategory(primaryCategory);
    }
  }, [primaryCategory, categories, categoryPreferences]);

  useEffect(() => {
    if (selectedCategory && selectedCategory.label !== starredCategory.label) {
      setRecentlyUnstarredSpaces([]);
    }
  }, [selectedCategory]);

  // Clear recently unstarred spaces when filters change
  useEffect(() => {
    setRecentlyUnstarredSpaces([]);
  }, [showArchived, sort, search]);

  useEffect(() => {
    setFuse(
      new Fuse(spaces, {
        threshold: 0.3,
        location: 0,
        distance: 1000,
        keys: ['name'],
        shouldSort: true,
      })
    );
  }, [spaces]);

  useEffect(() => {
    // Temp, To remove tabbar from the screen
    const tabbar = window.document.getElementById('space-settings-tabs');
    if (tabbar) {
      tabbar.style.display = 'none';
    }
    return () => {
      if (tabbar) {
        tabbar.style.display = 'block';
      }
    };
  }, []);

  const handleSpaceStar = async (spaceId, subCategory = null) => {
    const spaceFromProp = spaces.find((s) => s.id === spaceId);

    if (!spaceFromProp) return;

    if (spaceFromProp.isStarred) {
      // We are unstarring - add to recently unstarred list
      setRecentlyUnstarredSpaces((rus) => {
        // Prevent duplicates
        if (rus.some((s) => s.id === spaceId)) {
          return rus;
        }
        return [...rus, { ...spaceFromProp, isStarred: false }];
      });
    } else {
      // We are starring - remove from recently unstarred list immediately
      setRecentlyUnstarredSpaces((rus) => rus.filter((s) => s.id !== spaceId));
    }

    // Call the star/unstar operation (optimistic update happens in Redux)
    await onSpaceStar({
      spaceId,
      showArchived,
      sort,
      search,
      category: selectedCategory?.label || null,
      subCategory,
    });

    // Refresh recently viewed spaces
    fetchRecentlyViewedSpaces();
  };

  const handleSpaceSelect = (id, subCategory = null) => {
    onSpaceSelect(
      id,
      showArchived,
      sort,
      selectedCategory ? selectedCategory.label : null,
      subCategory
    );
  };

  // To handle the opening and closing of the accordions
  const handleOpenedAccordion = (uniqueCatagory) => {
    if (openedAccordions.has(uniqueCatagory)) {
      openedAccordions.delete(uniqueCatagory);
    } else {
      openedAccordions.add(uniqueCatagory);
    }
    setOpenedAccordions(new Set([...openedAccordions]));
  };

  const RenderStarredSpaces = () => {
    if (!selectedCategory) {
      return null;
    }
    // Use starredSpacesForDisplay - only shows starred spaces
    const spacesInStarredCategory = starredSpacesForDisplay;

    const allStaredArchived = spacesInStarredCategory?.every(
      (item) => item.archived === true
    );

    if (
      spacesInStarredCategory.length === 0 ||
      (allStaredArchived && !showArchived)
    ) {
      if (selectedCategory.label === starredCategory.label) {
        return <StarSpaceOnboarding />;
      }

      return (
        <Area>
          <Text>Your search didn’t match any of your spaces.</Text>
          <Text>Try with different keywords.</Text>
        </Area>
      );
    }

    return (
      <>
        <Accordion
          label={`Starred spaces (${spacesInStarredCategory.length})`}
          expanded={openedAccordions.has(starredCategory.label)}
          onChange={() => handleOpenedAccordion(starredCategory.label)}
          leftAligner
          detailsScrollAfter="20rem"
          size="large"
          sx={{
            bgcolor: 'transparent',
            '& .start-summary .MuiTypography-subtitle2': {
              fontSize: '20px',
              fontWeight: 600,
            },
          }}
        >
          <SpaceList
            spaces={spacesInStarredCategory}
            onClick={handleSpaceSelect}
            onStar={handleSpaceStar}
          />
        </Accordion>
        <Divider />
      </>
    );
  };

  const RenderPrimarySpacesAndSubSpaces = ({ category = null }) => {
    if (!selectedCategory) {
      return null;
    }
    const { label } = category;
    const subCategoriesOfCatagories = getSubCategories(spaces, label);
    const spacesAndSubSpacesAsPerCatagory = filterByCategory(
      allSpacesForDisplay,
      category
    );

    // Sub Catagory Spaces With Accordion
    if (subCategoriesOfCatagories.length > 0) {
      return subCategoriesOfCatagories.map((subCategory) => {
        const subCategorySpaces = filterByCategory(
          allSpacesForDisplay,
          category,
          subCategory
        );
        // Previous Academic Year Accordion
        return (
          <SpaceListAccordion
            key={subCategory.label}
            spaces={subCategorySpaces}
            label={subCategory.label}
            onClick={(id) => {
              handleSpaceSelect(id, subCategory.label);
            }}
            onStar={(id) => handleSpaceStar(id, subCategory.label)}
            expanded={openedAccordions.has(`${label}-${subCategory.label}`)}
            onChange={() => {
              handleOpenedAccordion(`${label}-${subCategory.label}`);
              if (openedAccordions.has(`${label}-${subCategory.label}`)) {
                onSubCategorySelect(label, subCategory.label);
              }
              setSubCategoriesPreference(subCategory);
            }}
          />
        );
      });
    }

    communitiesAndTrainingSpaces = spacesAndSubSpacesAsPerCatagory;
    // Communities And Traning Accordion Space List
    return (
      <SpaceList
        spaces={communitiesAndTrainingSpaces}
        onClick={handleSpaceSelect}
        onStar={handleSpaceStar}
      />
    );
  };

  const RenderPrimarySpaces = () => {
    return categories?.slice(1).map((category) => (
      <React.Fragment key={category.label}>
        <Accordion
          label={`${category.label} (${
            filterByCategory(allSpacesForDisplay, category).length
          })`}
          size="large"
          leftAligner
          detailsScrollAfter="20rem"
          expanded={openedAccordions.has(category.label)}
          onChange={() => handleOpenedAccordion(category.label)}
          sx={{
            bgcolor: 'transparent',
            '& .start-summary .MuiTypography-subtitle2': {
              fontSize: '20px',
              fontWeight: 600,
            },
          }}
        >
          <RenderPrimarySpacesAndSubSpaces category={category} />
        </Accordion>
        <Divider />
      </React.Fragment>
    ));
  };

  return (
    <Box
      bgcolor={palette.background.default}
      display="flex"
      flexDirection="column"
      width="100%"
      height="calc(100% - 67px)"
      withHeaderDivider
      marginBottom={8}
    >
      <RenderStarredSpaces />
      <RenderPrimarySpaces />
      <StaffSpaces showArchived={showArchived} />
    </Box>
  );
};

SpaceSelector.propTypes = {
  spaces: PropTypes.arrayOf(PropTypes.shape({})),
  onSpaceSelect: PropTypes.func,
  onSpaceStar: PropTypes.func,
  onSubCategorySelect: PropTypes.func,
  search: PropTypes.string,
  showArchived: PropTypes.bool,
  sort: PropTypes.oneOf(['az', 'za']),
  fetchRecentlyViewedSpaces: PropTypes.func,
};

SpaceSelector.defaultProps = {
  spaces: [],
  onSpaceSelect: () => null,
  onSpaceStar: () => null,
  onSubCategorySelect: () => null,
  search: '',
  showArchived: false,
  sort: 'az',
  fetchRecentlyViewedSpaces: () => null,
};

export default React.memo(SpaceSelector);
